// Trigger sets for the 5 launch reports — moved verbatim from lib/triggers.ts
// when the per-category trigger modules were introduced.

import type { TriggerQuestion } from "./types";
import { ASBESTOS, OCCUPIED, WAH, OVERHEAD, SILICA, ACCESS_OPTIONS, HOT_WORK_PERMIT_Q, COSHH_DETAIL_QS, RESCUE_Q, PRE2000_Q } from "./shared";

export const LAUNCH_TRIGGERS: Record<string, TriggerQuestion[]> = {
  "boiler-installation": [
    {
      key: "gasWork",
      label: "Is gas work involved?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-fire-emergency-ga-escape-uncontrolled-release"], scopeLine: "Gas work is carried out by a Gas Safe registered engineer (separate statutory competence)." },
        no: {
          hazardsOff: ["haz-fire-emergency-ga-escape-uncontrolled-release"],
          competenceOff: ["Gas Safe"],
          emergencyOff: ["Gas leak"],
          sequenceOff: [8],
          scopeLine: "No gas work in this package — any gas connection is outside this RAMS and would be carried out separately by a Gas Safe registered engineer.",
        },
      },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "gasSafeNumber",
            label: "Gas Safe registration number",
            kind: "text",
            placeholder: "e.g. 123456",
            requiredCheck: "Gas Safe registration number recorded",
            effects: { "*": { scopeLine: "Gas Safe registration: {answer}." } },
          },
        ],
      },
    },
    {
      key: "hotWorks",
      label: "Is hot work involved (soldering, brazing or cutting)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-hot-works-burn-from-hot-pipework-surface-and-flue-component"], permitsAdd: ["Hot work permit"] },
        no: { hazardsOff: ["haz-hot-works-burn-from-hot-pipework-surface-and-flue-component"], permitsRemove: ["Hot work permit"], emergencyOff: ["Fire from hot-works"], scopeLine: "No hot works — joints are made with cold methods (press-fit, push-fit or compression); a hot work permit is not required." },
      },
      notSure: {
        hazardsOn: ["haz-hot-works-burn-from-hot-pipework-surface-and-flue-component"],
        permitsAdd: ["Hot work permit"],
        failCheck: "Hot work requirement confirmed",
      },
      followUps: { when: "yes", questions: [HOT_WORK_PERMIT_Q] },
    },
    {
      key: "occupied",
      label: "Is the property occupied during the work?",
      kind: "yesno",
      effects: { yes: { hazardsOn: [OCCUPIED], requireFields: ["nearbyTradesOrPublic"] }, no: { hazardsOff: [OCCUPIED], scopeLine: "Premises unoccupied during the works — occupant-interface controls are not required for this task." } },
      notSure: { hazardsOn: [OCCUPIED], failCheck: "Occupancy during the works confirmed" },
    },
    PRE2000_Q([ASBESTOS], [ASBESTOS]),
    {
      key: "height",
      label: "Is work at height required (flue, loft or roof access)?",
      kind: "yesno",
      effects: { yes: { hazardsOn: [WAH] }, no: { hazardsOff: [WAH] } },
      notSure: { hazardsOn: [WAH], failCheck: "Work-at-height requirement confirmed" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "accessMethod",
            label: "What access equipment will be used?",
            kind: "choice",
            options: ACCESS_OPTIONS,
            effects: Object.fromEntries(ACCESS_OPTIONS.map((o) => [o, { scopeLine: `Access for work at height: ${o}.` }])),
          },
          RESCUE_Q,
        ],
      },
    },
    {
      key: "coshh",
      label: "Will you use flux, sealants, jointing compounds or system chemicals?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-coshh-coshh-flux-sealant-jointing-compound-and-plumbing-chemical"] },
        no: {
          hazardsOff: ["haz-coshh-coshh-flux-sealant-jointing-compound-and-plumbing-chemical"],
          emergencyOff: ["Chemical exposure"],
          scopeLine: "No flux, sealants, jointing compounds or system chemicals in use — COSHH controls are not required for this task.",
        },
      },
      notSure: {
        hazardsOn: ["haz-coshh-coshh-flux-sealant-jointing-compound-and-plumbing-chemical"],
        failCheck: "Substances in use confirmed",
      },
      followUps: { when: "yes", questions: COSHH_DETAIL_QS },
    },
  ],

  "consumer-unit-replacement": [
    {
      key: "occupied",
      label: "Are the premises occupied during the work?",
      kind: "yesno",
      effects: { yes: { hazardsOn: [OCCUPIED], requireFields: ["nearbyTradesOrPublic"] }, no: { hazardsOff: [OCCUPIED], scopeLine: "Premises unoccupied during the works — occupant-interface controls are not required for this task." } },
      notSure: { hazardsOn: [OCCUPIED], failCheck: "Occupancy during the works confirmed" },
    },
    PRE2000_Q([ASBESTOS], [ASBESTOS]),
    {
      key: "liveWork",
      label: "Is any live working proposed?",
      kind: "yesno",
      effects: {
        no: { scopeLine: "No live working — all circuits are isolated, locked off and proved dead (GS38) before work." },
      },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "liveJustification",
            label: "Justification for live working (EAWR 1989 reg 14)",
            kind: "text",
            placeholder: "Why is it unreasonable to work dead?",
            requiredCheck: "Live working justification recorded",
            effects: { "*": { scopeLine: "Live working is proposed. Justification (EAWR 1989 reg 14): {answer}. Additional controls per the risk assessment apply." } },
          },
        ],
      },
    },
    {
      key: "cableCondition",
      label: "Is the existing wiring in poor or unknown condition?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-electrical-contact-unsafe-or-damaged-cable-condition"] },
        no: { hazardsOff: ["haz-electrical-contact-unsafe-or-damaged-cable-condition"] },
      },
    },
    {
      key: "dnoTails",
      label: "Will meter tails or the DNO supply need to be disturbed?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Meter tails / supply disturbance — DNO or meter operator contacted in advance to arrange isolation or a tail shield." },
      },
      notSure: { failCheck: "DNO / meter tail disturbance confirmed" },
    },
    {
      key: "isolationRecord",
      label: "Will safe isolation be recorded (lock-off applied, proved dead with a GS38 device)?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Safe isolation recorded: lock-off and personal tag applied, circuits proved dead with a GS38-compliant device before work." },
        no: { failCheck: "Safe-isolation recording confirmed" },
      },
      notSure: { failCheck: "Safe-isolation recording confirmed" },
    },
  ],

  "pitched-roof-recover": [
    {
      key: "fragile",
      label: "Are fragile surfaces, skylights or rooflights present?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-working-at-height-fall-through-fragile-or-weakened-roof-surface"] },
        no: { hazardsOff: ["haz-working-at-height-fall-through-fragile-or-weakened-roof-surface"], scopeLine: "No fragile surfaces, skylights or rooflights present on or near the work area." },
      },
      notSure: { hazardsOn: ["haz-working-at-height-fall-through-fragile-or-weakened-roof-surface"], failCheck: "Fragile surfaces / rooflights confirmed" },
    },
    PRE2000_Q([ASBESTOS], [ASBESTOS]),
    {
      key: "publicBelow",
      label: "Are occupants, the public or other trades below or nearby?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [OCCUPIED, "haz-public-interface-falling-material-or-object-striking-person-below"], requireFields: ["nearbyTradesOrPublic"] },
        no: { hazardsOff: [OCCUPIED], scopeLine: "No occupants, public or other trades below or adjacent to the work area during the works." },
      },
      notSure: { hazardsOn: [OCCUPIED, "haz-public-interface-falling-material-or-object-striking-person-below"], failCheck: "Public / other trades below confirmed" },
    },
    {
      key: "overhead",
      label: "Are overhead power lines near the roof or scaffold?",
      kind: "yesno",
      effects: { yes: { hazardsOn: [OVERHEAD] }, no: { hazardsOff: [OVERHEAD], scopeLine: "No overhead power lines within risk distance of the work area." } },
      notSure: { hazardsOn: [OVERHEAD], failCheck: "Overhead power line proximity confirmed" },
    },
    {
      key: "cutting",
      label: "Will tiles or slates be cut or ground (dust)?",
      kind: "yesno",
      effects: { yes: { hazardsOn: [SILICA] }, no: { hazardsOff: [SILICA], scopeLine: "No cutting or grinding of tiles or slates — silica dust controls are not required for this task." } },
      notSure: { hazardsOn: [SILICA], failCheck: "Cutting / grinding requirement confirmed" },
    },
    {
      key: "accessMethod",
      label: "What access method will be used?",
      kind: "choice",
      options: ["Fixed scaffold with edge protection", "Mobile tower (PASMA)", "Ladder / roof ladder", "MEWP"],
      effects: {
        "Fixed scaffold with edge protection": { scopeLine: "Access: fixed scaffold with edge protection to the working perimeter." },
        "Mobile tower (PASMA)": { scopeLine: "Access: mobile access tower assembled and used by PASMA-trained operatives." },
        "Ladder / roof ladder": { scopeLine: "Access: ladders and roof ladders — short-duration work only, secured at the head." },
        MEWP: { scopeLine: "Access: MEWP operated by IPAF-trained operatives." },
      },
    },
    RESCUE_Q,
  ],

  "scaffold-erection": [
    {
      key: "design",
      label: "Is the scaffold standard TG20 compliant or bespoke designed?",
      kind: "choice",
      options: ["TG20 compliant", "Bespoke design"],
      effects: {
        "TG20 compliant": { scopeLine: "Scaffold configuration: standard TG20 compliant; TG20 compliance sheet held on site." },
        "Bespoke design": { scopeLine: "Scaffold configuration: bespoke design — design drawings and calculations must be on site before erection." },
      },
    },
    {
      key: "publicAreas",
      label: "Are public areas, pavements or roads affected?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [OCCUPIED], scopeLine: "Public interface — exclusion zones, signage and pavement management in place during erection.", requireFields: ["nearbyTradesOrPublic"] },
        no: { hazardsOff: [OCCUPIED], scopeLine: "No public areas, pavements or roads affected — public segregation and pavement licence controls are not required." },
      },
      notSure: { hazardsOn: [OCCUPIED], failCheck: "Public / pavement interface confirmed" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "pavementLicence",
            label: "Is a pavement licence or local-authority approval required?",
            kind: "yesno",
            effects: { yes: { permitsAdd: ["Pavement licence / local-authority approval"] } },
            notSure: { failCheck: "Pavement licence requirement confirmed with the local authority" },
          },
        ],
      },
    },
    {
      key: "overhead",
      label: "Are overhead power lines nearby?",
      kind: "yesno",
      effects: { yes: { hazardsOn: [OVERHEAD] }, no: { hazardsOff: [OVERHEAD], scopeLine: "No overhead power lines within risk distance of the work area." } },
      notSure: { hazardsOn: [OVERHEAD], failCheck: "Overhead power line proximity confirmed" },
    },
    {
      key: "vehicleDelivery",
      label: "Will materials be delivered or removed by vehicle or plant?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-plant-equipment-scaffold-tube-and-fitting-ejection-from-vehicle-or-plant"] },
        no: { hazardsOff: ["haz-plant-equipment-scaffold-tube-and-fitting-ejection-from-vehicle-or-plant"] },
      },
      notSure: { hazardsOn: ["haz-plant-equipment-scaffold-tube-and-fitting-ejection-from-vehicle-or-plant"], failCheck: "Vehicle / plant deliveries confirmed" },
    },
    {
      key: "ladderAccess",
      label: "Will ladders be used for access during erection?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-working-at-height-ladder-instability-or-unsafe-acces-equipment"] },
        no: { hazardsOff: ["haz-working-at-height-ladder-instability-or-unsafe-acces-equipment"] },
      },
      notSure: { hazardsOn: ["haz-working-at-height-ladder-instability-or-unsafe-acces-equipment"], failCheck: "Ladder access requirement confirmed" },
    },
    {
      key: "handover",
      label: "Will the scaffold be inspected, tagged and a handover certificate issued before first use?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Scaffold inspected and tagged (Scafftag) with a handover certificate before first use; re-inspected every 7 days and after alteration or adverse weather, inspections recorded." },
        no: { failCheck: "Scaffold handover inspection & tagging arranged" },
      },
      notSure: { failCheck: "Scaffold handover inspection & tagging arranged" },
    },
    RESCUE_Q,
  ],

  excavation: [
    {
      key: "services",
      label: "Are underground services present, or is their location unknown?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-excavation-contact-with-underground-service-during-excavation"] },
        no: { hazardsOff: ["haz-excavation-contact-with-underground-service-during-excavation"], scopeLine: "No underground services present in the work area — confirmed against service drawings before excavation." },
      },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "catScan",
            label: "Have a CAT/Genny scan and service-drawing review been completed?",
            kind: "yesno",
            effects: {
              yes: { scopeLine: "CAT/Genny scan and service-drawing review completed before excavation; permit to dig in place." },
              no: { failCheck: "CAT/Genny scan and service drawings confirmed" },
            },
          },
        ],
      },
    },
    {
      key: "entry",
      label: "Is confined space entry possible (chambers, deep or enclosed excavation)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-confined-space-confined-space-atmosphere-hazard"], permitsAdd: ["Confined space entry permit"] },
        no: { hazardsOff: ["haz-confined-space-confined-space-atmosphere-hazard"], permitsRemove: ["Confined space entry permit"], scopeLine: "No confined-space entry — chambers and deep or enclosed excavations are not part of this task." },
      },
      notSure: { hazardsOn: ["haz-confined-space-confined-space-atmosphere-hazard"], permitsAdd: ["Confined space entry permit"], failCheck: "Confined-space entry status confirmed" },
    },
    {
      key: "water",
      label: "Is water ingress or groundwater likely?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-excavation-flooding-and-groundwater-ingres-in-excavation"] },
        no: { hazardsOff: ["haz-excavation-flooding-and-groundwater-ingres-in-excavation"] },
      },
      notSure: { hazardsOn: ["haz-excavation-flooding-and-groundwater-ingres-in-excavation"], failCheck: "Groundwater risk confirmed" },
    },
    {
      key: "plant",
      label: "Will excavation plant or vehicles be used?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-plant-equipment-plant-and-vehicle-collision-with-pedestrian-or-worker"] },
        no: { hazardsOff: ["haz-plant-equipment-plant-and-vehicle-collision-with-pedestrian-or-worker"] },
      },
    },
    {
      key: "breaking",
      label: "Will concrete or hard ground be broken out (vibrating tools, dust)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [SILICA, "haz-noise-vibration-hand-arm-vibration-syndrome-hav"] },
        no: { hazardsOff: [SILICA, "haz-noise-vibration-hand-arm-vibration-syndrome-hav"] },
      },
    },
    {
      key: "support",
      label: "How will the excavation be supported?",
      kind: "choice",
      options: ["Battering / benching", "Trench box / shoring", "Less than 1.2 m — risk-assessed, no support"],
      effects: {
        "Battering / benching": { scopeLine: "Excavation support: sides battered or benched to a safe angle." },
        "Trench box / shoring": { scopeLine: "Excavation support: trench box / proprietary shoring installed progressively with the dig." },
        "Less than 1.2 m — risk-assessed, no support": { scopeLine: "Excavation is shallow (<1.2 m); no support required per risk assessment — conditions monitored throughout." },
      },
    },
    {
      key: "shiftInspection",
      label: "Will a competent person inspect the excavation at the start of each shift?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Excavation inspected by a competent person at the start of each shift and after rain, vibration or any event affecting stability; inspections recorded." },
        no: { failCheck: "Excavation inspection regime confirmed" },
      },
      notSure: { failCheck: "Excavation inspection regime confirmed" },
    },
  ],
};
